import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { followUpEvidenceQuestion, qaHref, QT_DAILY_COCKPIT_URL } from '../contextLinks';

type Status = 'open' | 'in_progress' | 'done' | 'dismissed';
type Source = 'review_extracted' | 'odoo_recommended' | 'manual';

interface ActionItem {
  id: number;
  description: string;
  source: Source;
  source_call_id: number | null;
  contact_id: number | null;
  owner_email: string | null;
  status: Status;
  due_date: string | null;
  notes: string | null;
  is_modified: boolean;
  modified_at: string | null;
  created_at: string;
  updated_at: string;
}

const OWNER_EMAILS: string[] = [
  'erik.carver@overlanddesignbuild.com',
  'randy.b@overlanddesignbuild.com',
  'vince.e@overlanddesignbuild.com',
  'nate.r@overlanddesignbuild.com',
  'ron.s@overlanddesignbuild.com',
];

const STATUSES: Status[] = ['open', 'in_progress', 'done', 'dismissed'];
const SOURCES: Source[] = ['review_extracted', 'odoo_recommended', 'manual'];

const STATUS_LABEL: Record<Status, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
  dismissed: 'Dismissed',
};

const SOURCE_LABEL: Record<Source, string> = {
  review_extracted: 'Review extracted',
  odoo_recommended: 'Odoo suggested',
  manual: 'Manual',
};

const STATUS_CHIP: Record<Status, string> = {
  open: 'bg-blue-50 ring-blue-200 text-blue-900',
  in_progress: 'bg-amber-50 ring-amber-200 text-amber-900',
  done: 'bg-green-50 ring-green-200 text-green-900',
  dismissed: 'bg-slate-100 ring-slate-300 text-slate-700',
};

const SOURCE_CHIP: Record<Source, string> = {
  review_extracted: 'bg-indigo-50 ring-indigo-200 text-indigo-900',
  odoo_recommended: 'bg-purple-50 ring-purple-200 text-purple-900',
  manual: 'bg-slate-100 ring-slate-300 text-slate-800',
};

interface Filters {
  status: Status | 'all';
  owner_email: string | 'all';
  source: Source | 'all';
  q: string;
  contact_id: string;
}

const DEFAULT_FILTERS: Filters = {
  status: 'all',
  owner_email: 'all',
  source: 'all',
  q: '',
  contact_id: '',
};

interface NewItemDraft {
  description: string;
  contact_id: string;
  owner_email: string;
  due_date: string;
}

const EMPTY_DRAFT: NewItemDraft = {
  description: '',
  contact_id: '',
  owner_email: '',
  due_date: '',
};

function chip(classes: string, label: string): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${classes}`}
    >
      {label}
    </span>
  );
}

function formatTimestamp(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatDate(date: string | null): string {
  if (date === null || date.length === 0) return 'No date';
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ownerName(email: string | null): string {
  if (email === null || email.length === 0) return 'Unassigned';
  const [local] = email.split('@');
  return local
    .split('.')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function sourceLabel(source: Source): string {
  return SOURCE_LABEL[source];
}

export function ActionItemsList(): JSX.Element {
  const [items, setItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [draft, setDraft] = useState<NewItemDraft>(EMPTY_DRAFT);
  const [creating, setCreating] = useState<boolean>(false);

  const queryString = useMemo<string>(() => {
    const params = new URLSearchParams();
    if (filters.status !== 'all') params.set('status', filters.status);
    if (filters.owner_email !== 'all') params.set('owner_email', filters.owner_email);
    if (filters.source !== 'all') params.set('source', filters.source);
    if (filters.q.trim().length > 0) params.set('q', filters.q.trim());
    if (filters.contact_id.trim().length > 0) params.set('contact_id', filters.contact_id.trim());
    params.set('limit', '200');
    return params.toString();
  }, [filters]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/action-items?${queryString}`, { credentials: 'include' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed: ${res.status}`);
      }
      const data = (await res.json()) as { action_items: ActionItem[] };
      setItems(data.action_items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patchItem(id: number, patch: Partial<ActionItem>): Promise<void> {
    const prev = items;
    setItems((curr) =>
      curr.map((it) => (it.id === id ? { ...it, ...patch, is_modified: true } : it)),
    );
    try {
      const res = await fetch(`/api/action-items/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed: ${res.status}`);
      }
    } catch (err) {
      setItems(prev);
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  }

  async function archiveItem(id: number): Promise<void> {
    const prev = items;
    setItems((curr) =>
      curr.map((it) => (it.id === id ? { ...it, status: 'dismissed' as Status } : it)),
    );
    try {
      const res = await fetch(`/api/action-items/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed: ${res.status}`);
      }
    } catch (err) {
      setItems(prev);
      setError(err instanceof Error ? err.message : 'Archive failed');
    }
  }

  async function createItem(): Promise<void> {
    if (draft.description.trim().length === 0) {
      setError('Description is required');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { description: draft.description.trim() };
      if (draft.contact_id.trim().length > 0) {
        const cid = Number(draft.contact_id.trim());
        if (!Number.isFinite(cid)) throw new Error('contact_id must be a number');
        body.contact_id = cid;
      }
      if (draft.owner_email.length > 0) body.owner_email = draft.owner_email;
      if (draft.due_date.length > 0) body.due_date = draft.due_date;
      const res = await fetch('/api/action-items', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(errBody.error ?? `Request failed: ${res.status}`);
      }
      setDraft(EMPTY_DRAFT);
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  }

  const visibleItems = useMemo<ActionItem[]>(() => {
    if (filters.status === 'dismissed') return items;
    return items.filter((it) => it.status !== 'dismissed');
  }, [items, filters.status]);

  const deskMetrics = useMemo(() => {
    const active = visibleItems.filter((it) => it.status !== 'done' && it.status !== 'dismissed');
    return {
      active: active.length,
      ownerless: active.filter((it) => it.owner_email === null).length,
      dated: active.filter((it) => it.due_date !== null).length,
      inProgress: visibleItems.filter((it) => it.status === 'in_progress').length,
    };
  }, [visibleItems]);

  const lanes = useMemo(() => {
    const laneOrder = [
      {
        id: 'needs_owner',
        title: 'Needs owner',
        description: 'Assign these before they drift.',
        accent: 'border-l-red-400',
      },
      {
        id: 'dated',
        title: 'Dated commitments',
        description: 'Follow-ups with a promised or scheduled date.',
        accent: 'border-l-amber-400',
      },
      {
        id: 'in_progress',
        title: 'Being handled',
        description: 'Already claimed by someone.',
        accent: 'border-l-blue-400',
      },
      {
        id: 'review',
        title: 'Ready to review',
        description: 'Open items that need a decision.',
        accent: 'border-l-slate-300',
      },
      {
        id: 'closed',
        title: 'Closed or archived',
        description: 'Kept visible only when filters include them.',
        accent: 'border-l-green-400',
      },
    ] as const;

    const grouped = new Map<string, ActionItem[]>();
    laneOrder.forEach((lane) => grouped.set(lane.id, []));

    visibleItems.forEach((item) => {
      let laneId: (typeof laneOrder)[number]['id'] = 'review';
      if (item.status === 'done' || item.status === 'dismissed') {
        laneId = 'closed';
      } else if (item.owner_email === null) {
        laneId = 'needs_owner';
      } else if (item.due_date !== null) {
        laneId = 'dated';
      } else if (item.status === 'in_progress') {
        laneId = 'in_progress';
      }
      grouped.get(laneId)?.push(item);
    });

    return laneOrder
      .map((lane) => ({ ...lane, items: grouped.get(lane.id) ?? [] }))
      .filter((lane) => lane.id !== 'closed' || lane.items.length > 0 || filters.status !== 'all');
  }, [filters.status, visibleItems]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Extracted-work review
            </p>
            <h1 className="text-2xl font-semibold text-slate-950">Reviewed follow-ups</h1>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              AI-extracted follow-up suggestions from call review, Odoo, and manual capture — review
              and disposition them here. This is a review desk, not a work tracker: the work itself is
              owned and closed in <span className="font-medium text-slate-800">Customer Workbench</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 sm:w-auto"
          >
            {showCreate ? 'Cancel capture' : 'Capture follow-up'}
          </button>
        </header>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-100 px-4 py-3 ring-1 ring-slate-200">
          <p className="text-sm text-slate-700">
            This is a <span className="font-medium">review desk</span>, not a work queue. To act on a
            follow-up — assign it, schedule it, and close it — work it in Customer Workbench.
          </p>
          <a
            href={QT_DAILY_COCKPIT_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
          >
            Open Workbench
          </a>
        </div>

        <section className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div className="text-xs font-medium text-slate-500">Active</div>
            <div className="mt-1 text-2xl font-semibold text-slate-950">
              {loading ? '—' : deskMetrics.active}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div className="text-xs font-medium text-slate-500">Needs owner</div>
            <div className="mt-1 text-2xl font-semibold text-red-700">
              {loading ? '—' : deskMetrics.ownerless}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div className="text-xs font-medium text-slate-500">Dated</div>
            <div className="mt-1 text-2xl font-semibold text-amber-700">
              {loading ? '—' : deskMetrics.dated}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div className="text-xs font-medium text-slate-500">In progress</div>
            <div className="mt-1 text-2xl font-semibold text-blue-700">
              {loading ? '—' : deskMetrics.inProgress}
            </div>
          </div>
        </section>

        <details className="mb-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <summary className="cursor-pointer text-sm font-semibold text-slate-900">
            Filters and maintenance
          </summary>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-5">
            <label className="flex flex-col text-xs font-medium text-slate-700">
              Status
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, status: e.target.value as Status | 'all' }))
                }
                className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              >
                <option value="all">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-xs font-medium text-slate-700">
              Owner
              <select
                value={filters.owner_email}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, owner_email: e.target.value }))
                }
                className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              >
                <option value="all">All</option>
                {OWNER_EMAILS.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-xs font-medium text-slate-700">
              Source
              <select
                value={filters.source}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, source: e.target.value as Source | 'all' }))
                }
                className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              >
                <option value="all">All</option>
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {sourceLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-xs font-medium text-slate-700">
              Search
              <input
                type="text"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="Description contains..."
                className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              />
            </label>
            <label className="flex flex-col text-xs font-medium text-slate-700">
              Contact ID
              <input
                type="text"
                value={filters.contact_id}
                onChange={(e) => setFilters((f) => ({ ...f, contact_id: e.target.value }))}
                placeholder="e.g. 123"
                className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
              />
            </label>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className="text-xs text-slate-600 hover:text-slate-900"
            >
              Reset filters
            </button>
            <span className="text-xs text-slate-500">
              {loading ? 'Loading...' : `${visibleItems.length} follow-ups`}
            </span>
          </div>
        </details>

        {error !== null ? (
          <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-200">
            {error}
          </div>
        ) : null}

        {showCreate ? (
          <section className="mb-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_160px_140px_auto] lg:items-end">
              <label className="flex flex-col text-xs font-medium text-slate-700">
                Follow-up
                    <input
                      type="text"
                      value={draft.description}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, description: e.target.value }))
                      }
                      placeholder="What needs doing? (required)"
                  className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-2 text-sm"
                    />
              </label>
              <label className="flex flex-col text-xs font-medium text-slate-700">
                Owner
                    <select
                      value={draft.owner_email}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, owner_email: e.target.value }))
                      }
                  className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-2 text-sm"
                    >
                  <option value="">Unassigned</option>
                      {OWNER_EMAILS.map((em) => (
                        <option key={em} value={em}>
                      {ownerName(em)}
                        </option>
                      ))}
                    </select>
              </label>
              <label className="flex flex-col text-xs font-medium text-slate-700">
                Due date
                    <input
                      type="date"
                      value={draft.due_date}
                      onChange={(e) => setDraft((d) => ({ ...d, due_date: e.target.value }))}
                  className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-2 text-sm"
                    />
              </label>
              <label className="flex flex-col text-xs font-medium text-slate-700">
                Contact ID
                    <input
                      type="text"
                      value={draft.contact_id}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, contact_id: e.target.value }))
                      }
                      placeholder="id"
                  className="mt-1 rounded-md border border-slate-300 bg-white px-2 py-2 text-sm"
                    />
              </label>
                    <button
                      type="button"
                      disabled={creating}
                      onClick={() => void createItem()}
                className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                {creating ? 'Saving...' : 'Save'}
                    </button>
            </div>
          </section>
        ) : null}

        <section className="space-y-4">
          {loading ? (
            <div className="rounded-lg bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-200">
              Loading follow-ups...
            </div>
          ) : null}

          {!loading && visibleItems.length === 0 ? (
            <div className="rounded-lg bg-white px-4 py-8 text-center text-sm text-slate-500 ring-1 ring-slate-200">
              No reviewed follow-ups match these filters.
            </div>
          ) : null}

          {!loading
            ? lanes.map((lane) => (
                <section key={lane.id} className="rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
                  <div className="flex flex-col gap-1 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-slate-950">{lane.title}</h2>
                      <p className="text-xs text-slate-500">{lane.description}</p>
                    </div>
                    <span className="text-xs font-medium text-slate-500">
                      {lane.items.length} {lane.items.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>

                  {lane.items.length === 0 ? (
                    <div className="px-4 py-5 text-sm text-slate-400">Clear.</div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {lane.items.map((it) => {
                        const isExpanded = expandedId === it.id;
                        return (
                          <article
                            key={it.id}
                            className={`border-l-4 ${lane.accent} px-4 py-4`}
                          >
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                              <button
                                type="button"
                                onClick={() => setExpandedId(isExpanded ? null : it.id)}
                                className="min-w-0 flex-1 text-left"
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  {chip(STATUS_CHIP[it.status], STATUS_LABEL[it.status])}
                                  {chip(SOURCE_CHIP[it.source], sourceLabel(it.source))}
                                  {it.is_modified ? (
                                    <span
                                      title={`Modified ${formatTimestamp(it.modified_at)}`}
                                      className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200"
                                    >
                                      Edited
                                    </span>
                                  ) : null}
                                </div>
                                <p className="mt-2 text-sm font-medium leading-6 text-slate-950">
                                  {it.description}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                                  <span>Owner: {ownerName(it.owner_email)}</span>
                                  <span>Due: {formatDate(it.due_date)}</span>
                                  <span>Updated: {formatTimestamp(it.updated_at)}</span>
                                  {it.contact_id !== null ? (
                                    <span>Contact: #{it.contact_id}</span>
                                  ) : null}
                                </div>
                              </button>

                              <div className="grid gap-2 sm:grid-cols-2 lg:w-[360px]">
                                <select
                                  aria-label={`Owner for follow-up ${it.id}`}
                                  value={it.owner_email ?? ''}
                                  onChange={(e) =>
                                    void patchItem(it.id, {
                                      owner_email: e.target.value === '' ? null : e.target.value,
                                    })
                                  }
                                  className="rounded-md border border-slate-200 bg-white px-2 py-2 text-xs text-slate-700"
                                >
                                  <option value="">Unassigned</option>
                                  {OWNER_EMAILS.map((em) => (
                                    <option key={em} value={em}>
                                      {ownerName(em)}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  aria-label={`Due date for follow-up ${it.id}`}
                                  type="date"
                                  value={it.due_date ?? ''}
                                  onChange={(e) =>
                                    void patchItem(it.id, {
                                      due_date: e.target.value === '' ? null : e.target.value,
                                    })
                                  }
                                  className="rounded-md border border-slate-200 bg-white px-2 py-2 text-xs text-slate-700"
                                />
                              </div>
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                              {it.contact_id !== null ? (
                                <Link
                                  to={`/contacts/${it.contact_id}`}
                                  className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                                >
                                  Customer evidence
                                </Link>
                              ) : null}
                              <Link
                                to={qaHref(
                                  followUpEvidenceQuestion({
                                    description: it.description,
                                    contactId: it.contact_id,
                                    source: sourceLabel(it.source),
                                    due: it.due_date,
                                  }),
                                )}
                                className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                              >
                                Ask evidence
                              </Link>
                              {it.status !== 'in_progress' && it.status !== 'done' ? (
                                <button
                                  type="button"
                                  onClick={() => void patchItem(it.id, { status: 'in_progress' })}
                                  className="rounded-md border border-blue-200 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
                                >
                                  Mark in progress
                                </button>
                              ) : null}
                              {it.status !== 'done' ? (
                                <button
                                  type="button"
                                  onClick={() => void patchItem(it.id, { status: 'done' })}
                                  className="rounded-md border border-green-200 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50"
                                >
                                  Mark done
                                </button>
                              ) : null}
                              {it.status !== 'dismissed' ? (
                          <button
                            type="button"
                            onClick={() => void archiveItem(it.id)}
                                  className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                          >
                            Archive
                          </button>
                        ) : null}
                            </div>

                            {isExpanded ? (
                              <div className="mt-4 rounded-md bg-slate-50 p-3 text-xs text-slate-700">
                                {it.source_call_id !== null ? (
                                  <div>
                                    <span className="font-semibold text-slate-900">
                                      Source call:
                                    </span>{' '}
                                    #{it.source_call_id}
                                  </div>
                                ) : null}
                                <div className="mt-2">
                                  <span className="font-semibold text-slate-900">Notes:</span>{' '}
                                  {it.notes !== null && it.notes.length > 0 ? (
                                    <span className="whitespace-pre-wrap">{it.notes}</span>
                                  ) : (
                                    <span className="text-slate-400">(none)</span>
                                  )}
                                </div>
                                <div className="mt-2 text-slate-500">
                                  Created {formatTimestamp(it.created_at)} · Updated{' '}
                                  {formatTimestamp(it.updated_at)}
                                </div>
                              </div>
                            ) : null}
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>
              ))
            : null}
        </section>
      </div>
    </div>
  );
}
